import { useMemo } from 'react';
import { Field, LifecycleRail, Money, Select, useAction, useInvalidateResource, useResourceList, useRelationLabel, type LifecycleStep, type ScreenContext } from '../../engine';
import { formatDate } from '../../lib/format';

// Opportunity detail header (ScreenDef.header): the pipeline stage rail built from the
// opportunity's pipeline stages, a "move to stage" select (POST :id/move-stage), and the deal
// line (value, probability, expected close, owner). Win / lose are the meta actions.

export interface StageRow {
  id: string;
  name: string;
  sort_order: number;
  probability: number;
  is_won: boolean;
  is_lost: boolean;
}

const INTERVAL: Record<string, string> = { monthly: '/mo', quarterly: '/qtr', semi_annual: '/6mo', annual: '/yr' };

export function stageRail(stages: StageRow[], current: string | null, status: string): LifecycleStep[] {
  const open = stages.filter((s) => !s.is_won && !s.is_lost);
  if (status === 'won' || status === 'lost') {
    return [...open.map((s) => ({ label: s.name, state: 'done' as const })), { label: status === 'won' ? 'Won' : 'Lost', state: 'terminal' as const }];
  }
  const idx = open.findIndex((s) => s.id === current);
  return open.map((s, i) => ({ label: s.name, state: i < idx ? 'done' : i === idx ? 'current' : 'upcoming' }));
}

export function OpportunityHeader({ id, row, refresh }: ScreenContext) {
  const pipelineId = (row?.pipeline_id as string | undefined) ?? '';
  const stages = useResourceList<StageRow>('pipeline-stages', { filter: [{ field: 'pipeline_id', op: 'eq', value: pipelineId }], sort: 'sort_order', limit: 50 }, { enabled: Boolean(pipelineId), staleTime: 60_000 });
  const invalidate = useInvalidateResource();
  const move = useAction('opportunities', 'move-stage', { toast: { success: 'Stage updated' }, onSuccess: () => { invalidate('opportunities', id); invalidate('opportunity-stage-history'); refresh(); } });
  const owner = useRelationLabel({ resource: 'technicians', displayField: 'name' }, (row?.owner_tech_id as string | null) ?? null);
  const status = String(row?.status ?? 'open');
  const closed = status === 'won' || status === 'lost';
  const steps = useMemo(() => stageRail(stages.rows, (row?.stage_id as string | null) ?? null, status), [stages.rows, row?.stage_id, status]);
  const currency = String(row?.currency ?? 'USD');
  const interval = row?.recurring_interval ? INTERVAL[String(row.recurring_interval)] ?? '' : '';
  return (
    <section className="hf-crm-header" aria-label="Opportunity summary">
      {steps.length > 0 && <LifecycleRail steps={steps} />}
      <div className="hf-between" style={{ flexWrap: 'wrap', gap: 12 }}>
        <div className="hf-row hf-num" style={{ gap: 16, flexWrap: 'wrap', fontSize: 'var(--t-13)' }}>
          <span>
            <span className="hf-caps">One-time</span> <Money minor={row?.one_time_minor as string} currency={currency} />
          </span>
          <span>
            <span className="hf-caps">Recurring</span> <Money minor={row?.recurring_minor as string} currency={currency} />
            {interval}
          </span>
          <span>
            <span className="hf-caps">Probability</span> {row?.probability == null ? '—' : `${String(row.probability)}%`}
          </span>
          <span>
            <span className="hf-caps">Expected close</span> {row?.expected_close ? formatDate(String(row.expected_close)) : '—'}
          </span>
          <span>
            <span className="hf-caps">Owner</span> {owner.data ?? (row?.owner_tech_id ? '…' : 'unassigned')}
          </span>
        </div>
        {!closed && (
          <Field label="Move to stage" inline>
            <Select
              size="sm"
              aria-label="Move to stage"
              value={String(row?.stage_id ?? '')}
              disabled={move.isPending || stages.isLoading}
              options={stages.rows.map((s) => ({ value: s.id, label: s.is_won ? `${s.name} (won)` : s.is_lost ? `${s.name} (lost)` : `${s.name} · ${s.probability}%` }))}
              onChange={(e) => {
                const stageId = e.target.value;
                if (stageId && stageId !== row?.stage_id) move.mutate({ id, body: { stage_id: stageId } });
              }}
            />
          </Field>
        )}
      </div>
    </section>
  );
}
